import { useEffect, useState, useMemo } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { X, Plus, MapPin, ArrowLeft } from "lucide-react";
import TextInput from "../../components/common/inputfields/TextInput";
import LocationInput from "../../components/common/inputfields/LocationInput";
import AdminSelect from "../../components/common/inputfields/AdminSelect";
import {
  addHomeAddress,
  fetchHomeAddressList,
  getAvailableCities,
  getAvailableStates,
} from "../../store/thunk/addressThunk";
import { selectUser } from "../../store/reducer/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { selectAuthLoading } from "../../store/reducer/authSlice";
import toast from "react-hot-toast";
import ButtonLoader from "../loader/ButtonLoader";

const AddHomeAddressModal = ({ open, setOpen }) => {
  const dispatch = useDispatch();
  const [error, setError] = useState(null);
  const [showAvailableCities, setShowAvailableCities] = useState(false);
  const user = useSelector(selectUser);
  const loading = useSelector(selectAuthLoading);
  const { availableCities, availableCitiesLoading, availableStates, availableStatesLoading } = useSelector(
    (state) => state.address
  );

  const [formData, setFormData] = useState({
    address: "",
    zipcode: "",
    city: "",
    state: "",
    apartmentGate: "",
    addressName: "",
    sameAsBilling: false,
  });

  // Fetch available cities and states when modal opens
  useEffect(() => {
    if (open) {
      if (!availableCities || availableCities.length === 0) {
        dispatch(getAvailableCities());
      }
      if (!availableStates || availableStates.length === 0) {
        dispatch(getAvailableStates());
      }
    }
  }, [open, dispatch, availableCities, availableStates]);

  // Create a lookup map for faster validation
  const cityZipMap = useMemo(() => {
    if (!availableCities || availableCities.length === 0) return null;

    const map = new Map();
    availableCities.forEach((location) => {
      const key = `${location.city.toLowerCase()}-${location.postal_code}`;
      if (!map.has(key)) {
        map.set(key, location);
      }
    });
    return map;
  }, [availableCities]);

  // Group cities with their postal codes for display
  const groupedCities = useMemo(() => {
    if (!availableCities || availableCities.length === 0) return [];

    const cityMap = new Map();
    availableCities.forEach((location) => {
      const cityName = location.city;
      if (!cityMap.has(cityName)) {
        cityMap.set(cityName, []);
      }
      cityMap.get(cityName).push(location.postal_code);
    });

    // Convert to array and sort by city name
    return Array.from(cityMap.entries())
      .map(([city, postalCodes]) => ({
        city,
        postalCodes: postalCodes.sort((a, b) => a - b),
      }))
      .sort((a, b) => a.city.localeCompare(b.city));
  }, [availableCities]);

  // Transform states data for dropdown
  const stateOptions = useMemo(() => {
    if (!availableStates || availableStates.length === 0) return [];

    return availableStates
      .filter(state => state.status === 1)
      .map(state => ({
        label: state.state_name,
        value: state.id.toString()
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [availableStates]);

  // Get state display value (convert ID to name or vice versa)
  const getStateDisplayValue = (stateValue) => {
    if (!stateValue) return '';

    // If the value is already a state name (from LocationInput), find the ID
    const isStateName = availableStates?.some(s => s.state_name === stateValue);
    if (isStateName) {
      const foundState = availableStates.find(s => s.state_name === stateValue);
      return foundState ? foundState.id.toString() : stateValue;
    }

    // Otherwise, it's an ID, return as is
    return stateValue.toString();
  };

  // Validate if the selected city and zipcode are available
  const validateCityAndZipcode = (city, zipcode) => {
    if (!cityZipMap)
      return { isValid: false, message: "Loading available locations..." };

    const key = `${city.toLowerCase()}-${zipcode}`;
    const location = cityZipMap.get(key);

    if (!location) {
      return {
        isValid: false,
        message: `Sorry, we don't currently deliver to ${city}, ${zipcode}. Please select a different location.`,
      };
    }

    if (location.status !== 1) {
      return {
        isValid: false,
        message: `Delivery is currently unavailable in ${city}, ${zipcode}.`,
      };
    }

    return { isValid: true, location };
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      address: "",
      zipcode: "",
      city: "",
      state: "",
      apartmentGate: "",
      addressName: "",
      sameAsBilling: false,
    });
    setError(null);
    setShowAvailableCities(false);
  };

  // Close modal with reset
  const handleClose = () => {
    if (!loading) {
      resetForm();
      setOpen(false);
    }
  };

  // Handle place selection from LocationInput
  const handlePlaceSelected = (place) => {
    // Validate city and zipcode before allowing autofill
    const validation = validateCityAndZipcode(place.city, place.zipcode);

    if (!validation.isValid) {
      setError(validation.message);
      toast.error(validation.message);

      // Clear the location fields
      setFormData((prev) => ({
        ...prev,
        address: "",
        zipcode: "",
        city: "",
        state: "",
      }));
      return;
    }

    // Find matching state ID from the dropdown options
    let stateId = place.state;
    if (place.state && availableStates) {
      const matchingState = availableStates.find(
        s => s.state_name === place.state || s.state_code === place.state
      );
      stateId = matchingState ? matchingState.id.toString() : place.state;
    }

    // If valid, populate the form
    setFormData((prev) => ({
      ...prev,
      address: place.street,
      zipcode: place.zipcode,
      city: place.city,
      state: stateId,
    }));

    // Clear error
    setError(null);
  };

  // Form validation
  const validateForm = () => {
    const requiredFields = [
      { field: "address", label: "Address" },
      { field: "zipcode", label: "Zipcode" },
      { field: "city", label: "City" },
      { field: "state", label: "State" },
      { field: "addressName", label: "Address Name" },
      { field: "apartmentGate", label: "Apartment Gate Code" },
    ];

    for (const { field, label } of requiredFields) {
      if (!formData[field] || formData[field].trim() === "") {
        const errorMessage = `Please fill in the ${label} field.`;
        setError(errorMessage);
        return false;
      }
    }

    // Final validation of city and zipcode
    const validation = validateCityAndZipcode(formData.city, formData.zipcode);
    if (!validation.isValid) {
      setError(validation.message);
      return false;
    }

    setError(null);
    return true;
  };

  const handleSubmit = () => {
    // Validate form first
    if (!validateForm()) {
      toast.error("Please fill in all required fields with valid locations");
      return;
    }

    const payload = {
      action: "add-home-delivery-address",
      user_id: user?.user_id,
      address: formData.address,
      address_name: formData.addressName,
      city: formData.city,
      gatecode: formData.apartmentGate,
      state: formData.state,
      zipcode: formData.zipcode,
    };

    const addAddressPromise = dispatch(addHomeAddress(payload)).unwrap();

    toast.promise(
      addAddressPromise.then((res) => {
        if (res.status === 0) {
          throw new Error(res.message || "Failed to add address.");
        }

        // Success - refresh address list
        dispatch(fetchHomeAddressList());

        // Reset form and close modal
        resetForm();
        setOpen(false);

        return res;
      }),
      {
        loading: "Adding address...",
        success: (res) => res.message || "Home address added successfully!",
        error: (err) =>
          err.message || "Failed to add address. Please try again.",
      },
      {
        success: {
          duration: 3000,
        },
        error: {
          duration: 4000,
        },
      }
    );
  };

  // Render available cities view
  const renderAvailableCitiesView = () => (
    <div className="space-y-4 px-4 pb-6">
      <div className="flex items-center mb-4">
        <button
          onClick={() => setShowAvailableCities(false)}
          className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Add Address
        </button>
      </div>

      <div className="mb-4">
        <p className="text-sm text-gray-600">
          We currently deliver to the following cities and postal codes:
        </p>
      </div>

      <div className="block h-[60vh] overflow-y-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                City
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Postal Codes
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {groupedCities.map((cityData, index) => (
              <tr
                key={index}
                className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {cityData.city}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {cityData.postalCodes.join(", ")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Render form view
  const renderFormView = () => (
    <div className="space-y-4 px-4">
      {/* Loading indicator for available cities */}
      {availableCitiesLoading && (
        <div className="text-gray-600 text-sm bg-blue-50 border border-blue-200 rounded-md p-2">
          Loading available delivery locations...
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Address <span className="text-red-500">*</span>
        </label>
        <LocationInput
          type="text"
          name="address"
          value={formData.address}
          onChange={handleInputChange}
          placeholder="Enter a location"
          required
          disabled={loading || availableCitiesLoading}
          onPlaceSelected={handlePlaceSelected}
        />
        {error && (
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setShowAvailableCities(true)}
              className="text-red-600 hover:text-red-800 text-sm font-medium underline mt-1 flex items-center"
            >
              <MapPin className="h-4 w-4 mr-1" />
              View Available Cities
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 space-x-3">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Zipcode <span className="text-red-500">*</span>
          </label>
          <TextInput
            type="text"
            name="zipcode"
            placeholder="Enter a ZipCode"
            value={formData.zipcode}
            onChange={handleInputChange}
            required
            readOnly={true}
            disabled={loading}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            City <span className="text-red-500">*</span>
          </label>
          <TextInput
            type="text"
            name="city"
            placeholder="Enter a City"
            value={formData.city}
            onChange={handleInputChange}
            required
            readOnly={true}
            disabled={loading}
          />
        </div>
      </div>

      <div>
        <AdminSelect
          label="State"
          name="state"
          placeholder="Select State"
          value={getStateDisplayValue(formData.state)}
          onChange={handleInputChange}
          options={stateOptions}
          loading={availableStatesLoading}
          helperText=""
          required
          readOnly
          className='border-b w-full border-b-gray-300'
          selectClassName='w-full border-0 px-0 pt-0 mb-0'
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Apartment Gate Code <span className="text-red-500">*</span>
        </label>
        <TextInput
          type="text"
          name="apartmentGate"
          placeholder="Enter a Gate Code"
          value={formData.apartmentGate}
          onChange={handleInputChange}
          disabled={loading}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Address Name <span className="text-red-500">*</span>
        </label>
        <TextInput
          type="text"
          name="addressName"
          value={formData.addressName}
          onChange={handleInputChange}
          placeholder="Eg: Home or Office"
          disabled={loading}
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          name="sameAsBilling"
          id="sameAsBilling"
          checked={formData.sameAsBilling}
          onChange={handleInputChange}
          disabled={loading}
          className="disabled:opacity-50"
        />
        <label htmlFor="sameAsBilling" className="text-sm text-gray-700">
          Same as billing address
        </label>
      </div>

      <div className="flex gap-3 py-4">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading || availableCitiesLoading}
          className={`flex-1 main-btn transition-colors ${
            loading || availableCitiesLoading
              ? "opacity-50 cursor-not-allowed"
              : "hover:opacity-90"
          }`}
        >
          {loading ? (
            <span className="flex justify-center items-center">
              <p className="px-2">Save</p>
              <ButtonLoader />
            </span>
          ) : (
            "Save"
          )}
        </button>
        <button
          type="button"
          onClick={handleClose}
          disabled={loading}
          className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-full font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Discard
        </button>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onClose={handleClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/50" />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
        <DialogPanel className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6 bg-red-gradient p-4">
            <DialogTitle className="text-md font-normal text-white flex items-center space-x-3">
              {showAvailableCities ? (
                <>
                  <MapPin className="h-6 w-6" />
                  <span className="text-lg">Available Delivery Cities</span>
                </>
              ) : (
                <>
                  <Plus className="h-6 w-6" />
                  <span className="text-lg">Add Home Address</span>
                </>
              )}
            </DialogTitle>
            <button
              onClick={handleClose}
              disabled={loading}
              className="text-white transition-colors disabled:opacity-50"
            >
              <X size={24} />
            </button>
          </div>

          {showAvailableCities ? renderAvailableCitiesView() : renderFormView()}
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default AddHomeAddressModal;